import { createSlice } from "@reduxjs/toolkit";
import { fetchGuidelines } from "./thunk/guidelinesThunk";
import type { GuidelineApiItem } from "@modules/public-comments/services/apiEndpoints";

type Status = "idle" | "loading" | "succeeded" | "failed";

interface GuidelinesState {
  items: GuidelineApiItem[];
  total: number;
  status: Status;
  error?: string;
}

const initialState: GuidelinesState = {
  items: [],
  total: 0,
  status: "idle",
  error: undefined
};

function normalizePayload(payload: any): {
  data: GuidelineApiItem[];
  total: number;
} {
  const unwrapAxios =
    payload?.data && !Array.isArray(payload?.data) ? payload.data : payload;
  const inner =
    unwrapAxios?.data &&
    (Array.isArray(unwrapAxios.data) ||
      unwrapAxios.data.permission_guidelines ||
      unwrapAxios.data.general_guidelines)
      ? unwrapAxios.data
      : unwrapAxios;

  if (Array.isArray(inner)) {
    return { data: inner, total: inner.length };
  }

  if (Array.isArray(inner?.permission_guidelines)) {
    return {
      data: inner.permission_guidelines,
      total: Number(inner?.total ?? inner.permission_guidelines.length)
    };
  }

  if (Array.isArray(inner?.general_guidelines)) {
    return {
      data: inner.general_guidelines,
      total: Number(inner?.total ?? inner.general_guidelines.length)
    };
  }

  if (Array.isArray(unwrapAxios)) {
    return { data: unwrapAxios, total: unwrapAxios.length };
  }

  return { data: [], total: 0 };
}

const guidelinesSlice = createSlice({
  name: "guidelines",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchGuidelines.pending, state => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(fetchGuidelines.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { data, total } = normalizePayload(action.payload);
        state.items = data;
        state.total = total;
      })
      .addCase(fetchGuidelines.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as string) ?? action.error.message ?? "Unknown error";
      });
  }
});

export default guidelinesSlice.reducer;
